import ParameterValidator from '@mvps-genesis/parameter-validator';
import decodeEntities from '@mvps-genesis/decode-entities';
import Splide from '@splidejs/splide';

export default class Carousel {

	// Default/fallback slider parameters
	defaultParameters = {
		arrows: true,
		breakpoints: {
			640: {
				gap: 16,
				perPage: 2
			},
			960: {
				gap: 16,
				perPage: 3
			}
		},
		gap: 8,
		keyboard: 'focused',
		pagination: false,
		perPage: 4,
		rewind: true
	};

	constructor (container, parameters) {

		// Validator
		this._validator = new ParameterValidator(this);

		// Splide elements to validate
		this.container = this._validator.isElement('container', container);
		this.track = this._validator.isElement('carousel track container', '.g-carousel__track', {container: this.container});
		this.slides = this._validator.isElement('carousel slides container', '.g-carousel__slides', {container: this.container});

		// Optional Splide elements
		this.arrows = this._validator.isElement('carousel arrows container', '.g-carousel__arrows', {container: this.container,
			required: false});

		// Carousel parameters set (via JS code) vs dataset (via template code). dataset value will take priority
		const options = this.container.dataset.options && this.container.dataset.options.length ? JSON.parse(decodeEntities(this.container.dataset.options)) : parameters;

		// Use default parameters if nothing is passed
		this.parameters = options ? options : this.defaultParameters;

		// Class to show and hide arrows
		this.hideArrowsClass = 'g-carousel__hide-arrows';

		// Attach classes and carousel specific attributes (Splide.js)
		this.build();

		// Initialize slider
		this.init();

	}

	build () {

		this.container.classList.add('splide');

		// Build default elements
		if (this.slides) {
			this.buildSlides();
		}

		if (this.track) {
			this.buildTrack();
		}

		// Build optional elements
		if (this.arrows) {
			this.buildArrows();
		} else if (!this.parameters.arrows) {
			this.parameters.arrows = false;
		}

		if (this.parameters.pagination) {
			if (!this.parameters.classes) {
				this.parameters.classes = {};
			}

			this.parameters.classes.pagination = 'g-carousel__bullets splide__pagination';
		}

	}

	buildArrows () {

		this.arrows.classList.add('splide__arrows');

		const arrowLeft = this.arrows.querySelector('.g-carousel__arrow--prev');
		const arrowRight = this.arrows.querySelector('.g-carousel__arrow--next');

		if (arrowLeft) {
			arrowLeft.classList.add('splide__arrow');
			arrowLeft.classList.add('splide__arrow--prev');
		}

		if (arrowRight) {
			arrowRight.classList.add('splide__arrow');
			arrowRight.classList.add('splide__arrow--next');
		}

	}

	buildBullets () {

		this.bullets.classList.add('splide__pagination');

	}

	buildSlides () {

		this.slides.classList.add('splide__list');

		this.slideElements = this.slides.getElementsByClassName('g-carousel__slide');
		this.slideCount = this.slideElements.length;

		for (const slideElement of this.slideElements) {
			slideElement.classList.add('splide__slide');
		}

	}

	buildTrack () {

		this.track.classList.add('splide__track');

	}

	destroy () {

		if (!this.container.classList.contains('splide')) {
			return false;
		}

		if (this.slider) {
			this.slider.destroy('completely');
		}

		return true;

	}

	init () {

		this.destroy();

		if (!this.parameters.keyboard) {
			this.parameters.keyboard = 'focused';
		}

		this.slider = new Splide(this.container, this.parameters);

		this.slider.on('mounted updated', () => this.determineArrowVisibility());

		this.bindSliderEvents();

		this.container.data = this.container.data || {};

		this.container.data.carousel = this.slider;

	}

	determineArrowVisibility () {
		if (this.slider.options.perPage >= this.slider.length) {
			this.container.classList.add(this.hideArrowsClass);
		} else {
			this.container.classList.remove(this.hideArrowsClass);
		}
	}

	bindSliderEvents () {

		this.slider.on('visible', (slide) => this.slideVisible(slide));

		this.slider.on('hidden', (slide) => this.slideHidden(slide));

		this.slider.on('mounted', () => this.bindClonedProductCardEvents());

	}

	slideVisible (slide) {

		if (!slide.slide) {
			return;
		}

		for (const element of this.getKeyboardFocusableElements(slide.slide)) {
			element.removeAttribute('tabindex');
		}

	}

	slideHidden (slide) {

		if (!slide.slide) {
			return;
		}

		for (const element of this.getKeyboardFocusableElements(slide.slide)) {
			element.setAttribute('tabindex', -1);
		}

	}

	getKeyboardFocusableElements (element) {
		return element.querySelectorAll('a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])');
	}

	async getCloneElements (elements) {
		if (!elements) {
			return;
		}

		const {getAbsoluteParent} = await import('@mvps-genesis/selector-helpers');
		const filteredElements = elements.filter((element) => {
			let parentElement = getAbsoluteParent(element, '.splide__slide--clone');
			let isClone = element.classList.contains('splide__slide--clone') || parentElement;

			if (isClone) {
				return element;
			}
		});

		return filteredElements
	}

	async bindClonedProductCardEvents () {
		const productCardElements = Array.from(this.container.getElementsByClassName('js-product-card'));

		if (productCardElements && productCardElements.length === 0) {
			return;
		}

		const cloneElements = await this.getCloneElements(productCardElements);

		for (const clone of cloneElements) {
			this.bindImageSwapElement(clone);
		}
	}

	async bindImageSwapElement (element) {
		// Element
		const imageSwapElement = element.querySelector('.js-image-swap');

		if (!imageSwapElement) {
			return;
		}

		// Module
		const {default: ImageSwap} = await import('custom-extensions/image-swap');
		new ImageSwap(imageSwapElement);
	}
}
